import convert from "convert-units";
import _ from "lodash";
import moment from "moment";
import { RPE_INTENSITY_COLORS } from "../../../constants";

export const convertToLb = (value) => convert(value).from("kg").to("lb");
export const roundValue = (value) => _.round(value, 1);
export const roundToThousand = (value) => `${_.round(value / 1000, 1)}k`;
export const upperCaseFirstChar = (str) =>
  str ? `${str.charAt(0).toUpperCase()}${str.slice(1)}` : "";

/**
 * Returns the urgency class for the "days until new program" indicator.
 * @param {number} needsWorkoutsOn - Days until a new program is required (0 = overdue)
 * @returns {"urgent"|"strong-warning"|"warning"|"normal"|"relaxed"}
 */
export const getWorkoutsUrgencyClass = (needsWorkoutsOn) => {
  if (needsWorkoutsOn === 0) return "urgent";
  if (needsWorkoutsOn <= 7) return "strong-warning";
  if (needsWorkoutsOn <= 14) return "warning";
  if (needsWorkoutsOn <= 30) return "normal";
  return "relaxed";
};

// Days-since pill: green "Today", amber for the first week, red after that.
// Never-logged athletes get no pill (the cell shows "—").
export const getDaysSincePill = (days) => {
  if (days === null || days === undefined) return null;
  if (days === "+30" || days > 30) return { label: "+30d", tone: "danger" };
  if (days <= 0) return { label: null, tone: "success" };
  return { label: `${days}d`, tone: days >= 7 ? "danger" : "warning" };
};

export const NON_TRAINING_DAILY_STAT_METRICS = new Set([
  "bodyWeight",
  "steps",
  "bodyMeasurements",
  "readiness",
  "bodyPhotos",
  "hrv",
  "nutrients",
  "restingHeartRate",
  "bodyFatPercentage",
  "mealPhotos",
  "sleep",
  "waterIntake",
  "menstrualCycle",
  "sessionRPE",
]);

// Coach Overview metrics: a tick per day the coach did the activity, plus
// the days since the last one.
export const COACH_ACTIVITY_METRICS = new Set([
  "coach-checkin-review",
  "coach-video-comment",
  "coach-photo-comment",
  "coach-video-feedback",
  "coach-chat",
]);

// Daily-stat metrics (same set as the API's NON_TRAINING_DAILY_STAT_METRICS):
// a value dated after today doesn't count until its day, so the grid hides it
// to agree with "Last logged" and the 30-day average.
const DAILY_STAT_METRICS = new Set([
  "bodyWeight",
  "steps",
  "bodyMeasurements",
  "readiness",
  "bodyPhotos",
  "hrv",
  "nutrients",
  "restingHeartRate",
  "bodyFatPercentage",
  "mealPhotos",
  "sleep",
  "waterIntake",
  "sessionRPE",
]);

export const isHiddenFutureDay = ({
  metric,
  idx,
  today,
  isCurrentMonth,
  isPreviousMonth,
}) =>
  DAILY_STAT_METRICS.has(metric) &&
  (isCurrentMonth ? idx + 1 > today : !isPreviousMonth);

export const getLastLoggedDays = (athlete) =>
  athlete.lastLoggedDays ?? athlete.lastActivityDays ?? athlete.lastWorkoutDays;

// "+30" counts as 31 and never-logged sorts last, so ascending order puts the
// most recent activity first.
export const daysSinceValue = (athlete) => {
  const days = getLastLoggedDays(athlete);
  if (days === null || days === undefined) return Number.POSITIVE_INFINITY;
  return days === "+30" ? 31 : days;
};

export const daysSinceFilterOptions = [
  { value: "", textKey: "coachingDashboard.daysSinceFilter.any" },
  { value: "7", textKey: "coachingDashboard.daysSinceFilter.over7" },
  { value: "14", textKey: "coachingDashboard.daysSinceFilter.over14" },
  { value: "30", textKey: "coachingDashboard.daysSinceFilter.over30" },
];

// Session RPE band color, same scale as the calendar's Session RPE card.
// Exposed as `--c`; the CSS tints it (fill, ring, text) for tokens and meters.
export const getRpeCellStyle = (rpe) => {
  const colors = [...RPE_INTENSITY_COLORS.slice(0, -1), "var(--rpe-10-color)"];
  const index = Math.min(Math.max(Math.trunc(rpe - 5), 0), colors.length - 1);
  return { "--c": colors[index] };
};

const BODY_PART_KEYS = [
  "chest",
  "rightArm",
  "leftArm",
  "neck",
  "waist",
  "hips",
  "leftThigh",
  "rightThigh",
  "leftCalf",
  "rightCalf",
];

export const generateTooltipHtml = (
  athlete,
  cycle,
  idx,
  activeMonth,
  values,
  referAsClients,
  metric,
  unitSelected,
  today,
  t
) => {
  const tooltipData = {
    workouts: () => {
      return (
        <>
          {cycle && (
            <div>
              <div>
                <span>
                  <b>{t("coachingDashboard.tooltip.program")}:</b> {cycle.name}
                </span>
                <span>
                  {cycle && cycle.program && (
                    <span> / {cycle.program.name}</span>
                  )}
                </span>
              </div>
              <div>
                <span>
                  <b>{t("coachingDashboard.tooltip.week")}: </b>
                </span>
                <span>{cycle.week}</span>
              </div>
              <div>
                <span>
                  <b>{t("coachingDashboard.tooltip.session")}: </b>
                </span>
                <span>{cycle.session}</span>
              </div>
            </div>
          )}
        </>
      );
    },
    nutrients: () => {
      const { proteins, fats, fibers, carbs } = values || {};
      const proteinsKcal = proteins ? proteins * 4 : 0;
      const carbsKcal = carbs ? carbs * 4 : 0;
      const fatsKcal = fats ? fats * 9 : 0;
      const kcal = proteinsKcal + carbsKcal + fatsKcal;
      return (
        <>
          {values && (
            <div className=" flex flex-col  gap-2 ">
              {proteins ? (
                <span className="block text-(--protein)">
                  P: {roundValue(proteins)}g
                </span>
              ) : null}
              {carbs ? (
                <span className="block text-(--carbs)">
                  C: {roundValue(carbs)}g
                </span>
              ) : null}
              {fats ? (
                <span className="block text-(--fat)">
                  F: {roundValue(fats)}g
                </span>
              ) : null}
              {fibers ? (
                <span>
                  {t("coachingDashboard.tooltip.fiber")}: {roundValue(fibers)}g
                </span>
              ) : null}
              {kcal > 0 ? (
                <span>
                  {t("coachingDashboard.tooltip.kcal")}:{roundValue(kcal)}
                </span>
              ) : null}
            </div>
          )}
        </>
      );
    },
    bodyMeasurements: () => {
      const unit =
        unitSelected === "imperial"
          ? t("coachingDashboard.units.inch")
          : t("coachingDashboard.units.cm");
      return (
        <>
          <div className="data flex flex-wrap items-center gap-2 bg-(--white-05)  rounded-(--border-radius) mt-2 p-2 w-fit max-w-116 ">
            {Object.keys(values).map((key) => {
              if (!BODY_PART_KEYS.includes(key)) return null;
              const partLabel = t(`coachingDashboard.bodyParts.${key}`);
              const value =
                unitSelected === "metric"
                  ? values[key]
                  : convert(values[key]).from("cm").to("in");
              return (
                <span key={key} className="w-36 text-left">
                  {partLabel}:{` ${_.round(value, 2)} ${unit}`}
                </span>
              );
            })}
          </div>
        </>
      );
    },
    readiness: () => {
      return (
        <>
          <div className=" flex flex-col gap-y-2 mt-2 max-w-108">
            {Object.keys(values).map((key) => {
              if (!values[key]) return null;
              const text = t(`readinessRate.${key}`, { ns: "athlete-portal" });
              return (
                <span key={key}>
                  {text}: {values[key]}
                </span>
              );
            })}
          </div>
        </>
      );
    },
    "check-in": () => {
      if (values === "is-late")
        return (
          <div>
            {t("coachingDashboard.tooltip.daysLate", {
              count: today - (idx + 1),
            })}
          </div>
        );
    },
    "coach-notes": () => {
      return (
        <div>
          {t("coachingDashboard.tooltip.type")}: {upperCaseFirstChar(values)}
        </div>
      );
    },
  };

  const roleLabel = referAsClients
    ? t("athletes.roleClient")
    : t("athletes.roleAthlete");

  return (
    <div>
      <div>
        <span>
          <b>{t("coachingDashboard.tooltip.date")}: </b>
        </span>
        <span>{moment(activeMonth).add(idx, "days").format("MMM Do")}</span>
      </div>
      <div>
        <span>
          <b>{roleLabel}: </b>
        </span>
        <span>{athlete.name}</span>
      </div>
      {tooltipData[metric] ? tooltipData[metric]() : null}
    </div>
  );
};

export function getGroupOptions(t, clientWord, athletesGroups, canViewAll) {
  const base = canViewAll
    ? [
        {
          text: t("coachingDashboard.allRole", { role: clientWord }),
          value: "all",
        },
        {
          text: t("coachingDashboard.allOrganizationRole", {
            role: clientWord,
          }),
          value: "organization",
          href: "/actions/refresh?filterGroup=organization",
        },
      ]
    : [
        {
          text: t("coachingDashboard.allRole", { role: clientWord }),
          value: "all",
        },
      ];
  const groups = (athletesGroups || []).map((g) => ({
    text: g.name,
    value: g.name,
  }));
  return [...base, ...groups];
}

export const sortItems = [
  {
    titleKey: "coachingDashboard.sortItems.nameAsc",
    altKey: "coachingDashboard.sortItems.ascAlt",
    src: "/img/svg/sort-amount-up.svg",
    className: "icon-white",
    id: JSON.stringify([["name"], ["asc"]]),
  },
  {
    titleKey: "coachingDashboard.sortItems.nameDesc",
    altKey: "coachingDashboard.sortItems.descAlt",
    src: "/img/svg/sort-amount-down.svg",
    className: "icon-white",
    id: JSON.stringify([["name"], ["desc"]]),
  },
  {
    titleKey: "coachingDashboard.sortItems.dateAsc",
    altKey: "coachingDashboard.sortItems.dateAscAlt",
    src: "/img/svg/sort-amount-up.svg",
    className: "icon-white",
    id: JSON.stringify([["_id"], ["asc"]]),
  },
  {
    titleKey: "coachingDashboard.sortItems.dateDesc",
    altKey: "coachingDashboard.sortItems.dateDescAlt",
    src: "/img/svg/sort-amount-down.svg",
    className: "icon-white",
    id: JSON.stringify([["_id"], ["desc"]]),
  },
];
export const metricsOptions = [
  {
    textKey: "coachingDashboard.metrics.workouts",
    value: "workouts",
  },
  {
    textKey: "coachingDashboard.metrics.checkIn",
    value: "check-in",
  },
  {
    textKey: "coachingDashboard.metrics.sessionRPE",
    value: "sessionRPE",
  },
  {
    textKey: "coachingDashboard.metrics.videos",
    value: "videos",
  },
  {
    textKey: "coachingDashboard.metrics.bodyWeight",
    value: "bodyWeight",
  },
  {
    textKey: "coachingDashboard.metrics.bodyFatPercentage",
    value: "bodyFatPercentage",
  },
  {
    textKey: "coachingDashboard.metrics.bodyMeasurements",
    value: "bodyMeasurements",
  },
  {
    textKey: "coachingDashboard.metrics.bodyPhotos",
    value: "bodyPhotos",
  },
  {
    textKey: "coachingDashboard.metrics.sleep",
    value: "sleep",
  },
  {
    textKey: "coachingDashboard.metrics.readiness",
    value: "readiness",
  },
  {
    textKey: "coachingDashboard.metrics.hrv",
    value: "hrv",
  },
  {
    textKey: "coachingDashboard.metrics.restingHeartRate",
    value: "restingHeartRate",
  },
  {
    textKey: "coachingDashboard.metrics.steps",
    value: "steps",
  },
  {
    textKey: "coachingDashboard.metrics.menstrualCycle",
    value: "menstrualCycle",
  },
  {
    textKey: "coachingDashboard.metrics.nutrients",
    value: "nutrients",
  },
  {
    textKey: "coachingDashboard.metrics.waterIntake",
    value: "waterIntake",
  },
  {
    textKey: "coachingDashboard.metrics.mealPhotos",
    value: "mealPhotos",
  },
];
export const coachMetricsOptions = [
  {
    textKey: "coachingDashboard.coachMetrics.coachNotes",
    value: "coach-notes",
  },
  {
    textKey: "coachingDashboard.coachMetrics.checkInReview",
    value: "coach-checkin-review",
  },
  {
    textKey: "coachingDashboard.coachMetrics.videoComment",
    value: "coach-video-comment",
  },
  {
    textKey: "coachingDashboard.coachMetrics.photoComment",
    value: "coach-photo-comment",
  },
  {
    textKey: "coachingDashboard.coachMetrics.videoFeedback",
    value: "coach-video-feedback",
  },
  {
    textKey: "coachingDashboard.coachMetrics.chatMessage",
    value: "coach-chat",
  },
];

export const unitOptions = {
  bodyWeight: [
    { nameKey: "coachingDashboard.units.kg", unit: "metric", value: "metric" },
    {
      nameKey: "coachingDashboard.units.lb",
      unit: "imperial",
      value: "imperial",
    },
  ],
  bodyMeasurements: [
    {
      nameKey: "coachingDashboard.units.cm",
      unit: "metric",
      value: "metric",
    },
    {
      nameKey: "coachingDashboard.units.inch",
      unit: "imperial",
      value: "imperial",
    },
  ],
};

export const metricBaseUrl = {
  workouts: "/athlete-portal/calendar",
  "check-in": "/athlete-portal/metrics/check-in-summary",
  bodyWeight: "/athlete-portal/metrics/bodyWeight",
  steps: "/athlete-portal/metrics/step-count",
  nutrients: "/athlete-portal/nutrition",
  bodyMeasurements: "/athlete-portal/metrics/body-measurements",
  readiness: "/athlete-portal/metrics/readiness-rate",
  hrv: "/athlete-portal/metrics/hrv",
  bodyPhotos: "/athlete-portal/metrics/progress-photos",
  videos: "/athlete-portal/metrics/video-list",
  "coach-notes": "/athlete-portal/calendar",
  sessionRPE: "/athlete-portal/calendar",
  "coach-checkin-review": "/athlete-portal/metrics/check-in-summary",
  "coach-video-comment": "/athlete-portal/metrics/video-list",
  "coach-photo-comment": "/athlete-portal/metrics/progress-photos",
  "coach-video-feedback": "/athlete-portal/profile",
  "coach-chat": "/chat",
  mealPhotos: "/athlete-portal/metrics/meal-photos",
  restingHeartRate: "/athlete-portal/metrics/resting-heart-rate",
  bodyFatPercentage: "/athlete-portal/metrics/body-fat",
  sleep: "/athlete-portal/metrics/sleep-time",
  waterIntake: "/athlete-portal/metrics/water-intake",
  menstrualCycle: "/athlete-portal/metrics/female-cycle",
};

export const metricConfig = {
  workouts: {
    dataKey: "scheduled",
    getConfig: ({
      athlete,
      idx,
      isCurrentMonth,
      today,
      isPreviousMonth,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      const cycle = athlete.cycles[idx];
      const isComplete = athlete.scheduled[idx] === "Complete";
      let className = isComplete ? "value-Completed" : "value-late";
      // A finished workout stays a ✓ today, matching the "Done" count.
      if (isCurrentMonth) {
        if (today === idx + 1 && !isComplete) className = "value-In Progress";
        else if (today < idx + 1) className = "value-Upcoming";
      } else if (!isPreviousMonth) className = "value-Upcoming";
      let hRef = `/athlete-portal/calendar?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;

      return { className, cycle, hRef };
    },
  },
  "check-in": {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef =
        athlete.days[idx]?.status === "completed"
          ? `/athlete-portal/metrics/check-in-summary?athlete=${athlete._id}&checkInDate=${dateParam}&view=day&showBack=true`
          : "";

      if (isFromOtherOrganizationMember && !!hRef)
        hRef += `&organizationCoach=${organizationCoachId}`;

      return {
        className: {
          completed: "value-Completed",
          uncompleted: "value-not-Completed",
          upcoming: "value-Upcoming",
          "in-progress": "value-In ",
          "is-late": "value-late",
        }[athlete.days[idx]?.status],
        hRef,
      };
    },
  },

  bodyWeight: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,

      unitSelected,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/bodyWeight?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;

      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "",
        hRef,
        displayValue:
          unitSelected === "metric"
            ? roundValue(athlete.days[idx]?.value)
            : roundValue(convertToLb(athlete.days[idx]?.value)),
      };
    },
  },
  steps: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/step-count?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;

      return {
        className: "",
        hRef,
        displayValue:
          athlete.days[idx]?.value > 0
            ? roundToThousand(athlete.days[idx]?.value)
            : false,
      };
    },
  },
  nutrients: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      const dayValues = athlete.days[idx]?.value || {};
      const hasValues =
        (dayValues.fats && dayValues.fats > 0) ||
        (dayValues.carbs && dayValues.carbs > 0) ||
        (dayValues.proteins && dayValues.proteins > 0) ||
        (dayValues.fibers && dayValues.fibers > 0);
      let hRef = hasValues
        ? `/athlete-portal/nutrition?athlete=${athlete._id}&utcDate=${dateParam}&view=day&showBack=true`
        : "";
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: hasValues ? "value-Completed" : "",
        hRef,
      };
    },
  },
  bodyMeasurements: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/body-measurements?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
      };
    },
  },
  readiness: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/readiness-rate?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue: Object.values(athlete.days[idx].value)?.reduce(
          (prev, next) => prev + next
        ),
      };
    },
  },
  hrv: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/hrv?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue:
          athlete.days[idx]?.value > 0
            ? roundValue(athlete.days[idx].value)
            : false,
      };
    },
  },
  bodyPhotos: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/progress-photos?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
      };
    },
  },
  videos: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/video-list?athlete=${athlete._id}&date=${dateParam}&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
      };
    },
  },
  "coach-notes": {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      const isCalendar = athlete.days[idx]?.value === "calendar";
      let hRef = `/athlete-portal/${
        isCalendar ? "calendar" : "metrics/check-in-summary"
      }?athlete=${athlete._id}&${
        isCalendar ? "date" : "checkInDate"
      }=${dateParam}${isCalendar ? "&view=day" : ""}&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;

      return {
        className: "value-Completed",
        hRef,
      };
    },
  },
  sessionRPE: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/calendar?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue: athlete.days[idx]?.value,
        displayClassName: `rpe-pill${athlete.days[idx]?.value >= 9 ? " hi" : ""}`,
        displayStyle: getRpeCellStyle(athlete.days[idx]?.value),
      };
    },
  },
  ...Object.fromEntries(
    Object.entries({
      "coach-checkin-review": "/athlete-portal/metrics/check-in-summary",
      "coach-video-comment": "/athlete-portal/metrics/video-list",
      "coach-photo-comment": "/athlete-portal/metrics/progress-photos",
      "coach-video-feedback": "/athlete-portal/profile",
      "coach-chat": "/chat",
    }).map(([metricKey, path]) => [
      metricKey,
      {
        dataKey: "days",
        getConfig: ({
          athlete,
          isFromOtherOrganizationMember,
          organizationCoachId,
          dateParam,
        }) => {
          let hRef = `${path}?athlete=${athlete._id}&date=${dateParam}&showBack=true`;
          if (isFromOtherOrganizationMember)
            hRef += `&organizationCoach=${organizationCoachId}`;
          return { className: "value-Completed", hRef };
        },
      },
    ])
  ),
  mealPhotos: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/meal-photos?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true&startDate=${moment(dateParam).startOf("month").format("YYYYMMDD")}&endDate=${moment(dateParam).endOf("month").format("YYYYMMDD")}`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue:
          athlete.days[idx]?.value > 0 ? athlete.days[idx].value : false,
      };
    },
  },
  restingHeartRate: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/resting-heart-rate?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue:
          athlete.days[idx]?.value > 0
            ? roundValue(athlete.days[idx].value)
            : false,
      };
    },
  },
  bodyFatPercentage: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/body-fat?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      return {
        className: "value-Completed",
        hRef,
        displayValue:
          athlete.days[idx]?.value > 0
            ? roundValue(athlete.days[idx].value)
            : false,
      };
    },
  },
  sleep: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/sleep-time?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      const ms = athlete.days[idx]?.value;
      return {
        className: "value-Completed",
        hRef,
        displayValue: ms > 0 ? `${roundValue(ms / 3.6e6)}h` : false,
      };
    },
  },
  waterIntake: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/water-intake?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      const liters = athlete.days[idx]?.value;
      return {
        className: "value-Completed",
        hRef,
        displayValue: liters > 0 ? `${roundValue(liters)}L` : false,
      };
    },
  },
  menstrualCycle: {
    dataKey: "days",
    getConfig: ({
      athlete,
      idx,
      isFromOtherOrganizationMember,
      organizationCoachId,
      dateParam,
    }) => {
      let hRef = `/athlete-portal/metrics/female-cycle?athlete=${athlete._id}&date=${dateParam}&view=day&showBack=true`;
      if (isFromOtherOrganizationMember)
        hRef += `&organizationCoach=${organizationCoachId}`;
      const intensity = athlete.days[idx]?.value;
      return {
        className: intensity
          ? `value-Completed cycle-flow-${intensity}`
          : "value-late",
        hRef,
      };
    },
  },
};
